import { omit } from "lodash";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  AppointmentType,
  AppointmentTypeInput,
  AppointmentTypesFilters,
  AppointmentTypesResponse
} from "./types/appointment-types.type";

const APPOINTMENT_TYPES_QUERY =
  "{id,name,color,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{product{id,cpt_code{id,code},display_name,price}},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,limit_to_appointment_blocks,allow_other_types_during_block,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},is_virtual}";
export const APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{product{id,cpt_code{id,code},display_name,price}},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,limit_to_appointment_blocks,allow_other_types_during_block,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},is_virtual}";
export const GET_APPOINTMENT_TYPE_QUERY =
  "{id,name,color,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,allow_other_types_during_block,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,ambient_scribe_template_id,reservation_deposit_amount,allowed_staff_members,clinics}";
export const GET_APPOINTMENT_TYPE_QUERY_WITH_DEFAULT_NOTES =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,allow_other_types_during_block,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,reservation_deposit_amount,allowed_staff_members,clinics}";
// Narrow projection for "which appointment types reference a given scribe template".
// The list endpoint has no ambient_scribe_template filter, so callers match client-side.
// No is_active filter is applied by design — inactive types hold the same reference.
// `color` feeds the appointment-type swatch in the SN-19067 scribe-template surfaces.
export const APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LINKS =
  "{id,name,color,is_active,ambient_scribe_template_id}";
// The AVS-aware superset — only request it behind AFTER_VISIT_SUMMARY_FEATURE: naming
// `after_visit_summary_template_id` against a backend that predates the field 400s the whole read.
export const APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LINKS_WITH_AVS =
  "{id,name,color,is_active,ambient_scribe_template_id,after_visit_summary_template_id}";
// Same relation, but the paused-edit metadata row shows a duration and no swatch.
export const APPOINTMENT_TYPES_QUERY_SCRIBE_TEMPLATE_LABEL =
  "{id,name,default_duration,ambient_scribe_template_id}";
export const GET_APPOINTMENT_TYPE_QUERY_WITH_DEFAULT_NOTES_AND_TEMPLATE =
  "{id,name,color,default_appointment_notes,default_chart_note,default_duration,general_type,is_opportunity,is_audiologists_only,is_active,default_products{quantity,product{id,cpt_code{id,code},display_name,price,is_active,type}},notification_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_request_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},reminder_acknowledgment_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},appointment_information_message_template{id,type,template,subject_template,email_text_template,email_html_template,email_design_json},generate_sale,generate_chart_note,patient_notifications_enabled,intake_forms,google_review_request_message_disabled,ws_title,ws_description,ws_configuration,is_web_schedule,is_clinic_schedule,limit_to_appointment_blocks,allow_other_types_during_block,prompt_next_on_complete,next_appointment_type{id,name,color,default_duration,is_active},patient_visibility,is_virtual,ambient_scribe_template_id,reservation_deposit_amount,allowed_staff_members,clinics}";

// Appends the per-type follow-up interval read key to a RestQL projection.
// Kept OUT of the default query strings on purpose: RestQL 404s
// (`FieldNotFound`) on unknown fields, so the key may only ship on surfaces
// gated by FIND_NEXT_AVAILABLE_CHECKOUT_FEATURE — which ops turns on only
// after the backend field is deployed.
export const withNextAppointmentIntervalField = (query: string) =>
  query.replace(/}$/, ",next_appointment_interval_months}");

// Same convention for the After Visit Summary template slot — the read key rides
// AFTER_VISIT_SUMMARY_FEATURE, which ops turns on only after the backend field is deployed.
export const withAfterVisitSummaryTemplateField = (query: string) =>
  query.includes("after_visit_summary_template_id")
    ? query
    : query.replace(/}$/, ",after_visit_summary_template_id}");

export const APPOINTMENT_TYPES_QUERY_WITH_NEXT_INTERVAL =
  withNextAppointmentIntervalField(APPOINTMENT_TYPES_QUERY);

// One params builder for every checkout surface (Schedule Next modal, its
// prompt hook, the Current Visit drawer block) so they share a single RTK
// Query cache entry whether or not the interval field is being read.
export const getCheckoutAppointmentTypesParams = (
  includeNextInterval: boolean
): AppointmentTypesFilters => ({
  is_active: true,
  ...(includeNextInterval && {
    query: APPOINTMENT_TYPES_QUERY_WITH_NEXT_INTERVAL
  })
});

export const appointmentTypesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getAppointmentTypes: builder.query<
      AppointmentTypesResponse,
      AppointmentTypesFilters
    >({
      query: ({
        is_active,
        type,
        query = APPOINTMENT_TYPES_QUERY,
        exclude_type,
        is_clinic_schedule,
        is_web_schedule
      } = {}) => ({
        url: `/appointment-types/?${formatValidQueryParamUrlCommaSeparated({
          type,
          is_active,
          query,
          exclude_type,
          is_clinic_schedule,
          is_web_schedule
        })}`
      }),
      providesTags: (result) =>
        result?.map((appointmentType) => ({
          type: "AppointmentTypes" as const,
          id: appointmentType.id
        })) ?? []
    }),
    getAppointmentType: builder.query<
      AppointmentType,
      { id: number; query?: string }
    >({
      query: ({ id, query = GET_APPOINTMENT_TYPE_QUERY }) =>
        `/appointment-types/${id}/?query=${query}`,
      providesTags: (result) => [
        "AppointmentTypes",
        { type: "AppointmentTypes", id: result?.id }
      ]
    }),
    addAppointmentType: builder.mutation<AppointmentType, AppointmentTypeInput>(
      {
        query: (appointmentTypeData) => ({
          url: "/appointment-types/",
          method: "POST",
          body: appointmentTypeData
        }),
        invalidatesTags: ["AppointmentTypes"]
      }
    ),
    editAppointmentType: builder.mutation<
      AppointmentType,
      AppointmentTypeInput
    >({
      query: (appointmentTypeData) => ({
        url: `/appointment-types/${appointmentTypeData.id}/`,
        method: "PATCH",
        body: omit(appointmentTypeData, "id")
      }),
      invalidatesTags: ["AppointmentTypes"]
    }),
    resetTelehealthTemplates: builder.mutation<
      { reset_count: number },
      { id: number; message_types?: string[] }
    >({
      query: ({ id, message_types }) => ({
        url: `/appointment-types/${id}/reset-telehealth-templates/`,
        method: "POST",
        ...(message_types?.length && { body: { message_types } })
      }),
      invalidatesTags: ["AppointmentTypes", "MessageTemplates"]
    }),
    // Minimal partial PATCH so the scribe-template wizard can set an appointment
    // type's default template without refetching/resubmitting the whole object.
    setAppointmentTypeScribeTemplate: builder.mutation<
      AppointmentType,
      { id: number; ambient_scribe_template_id: number | null }
    >({
      query: ({ id, ambient_scribe_template_id }) => ({
        url: `/appointment-types/${id}/`,
        method: "PATCH",
        body: { ambient_scribe_template_id }
      }),
      invalidatesTags: ["AppointmentTypes"]
    })
  })
});

export const {
  useGetAppointmentTypesQuery,
  useAddAppointmentTypeMutation,
  useEditAppointmentTypeMutation,
  useGetAppointmentTypeQuery,
  useLazyGetAppointmentTypeQuery,
  useResetTelehealthTemplatesMutation,
  useSetAppointmentTypeScribeTemplateMutation
} = appointmentTypesApi;
